/**
 * Modal surfaces: the bottom sheet, and the four states every screen has to be able to be
 * in — empty, loading, failed, and busy.
 *
 * They live together because they share one visual grammar (full hairline border, faint
 * tinted fill, rounded corners, uppercase kicker) and one behavioural grammar (scrim, focus
 * trap, inert background, Escape to leave), and neither stays consistent unless it is
 * written once.
 *
 * The three things that make a web sheet feel like a native one, and are usually missing:
 *
 * - **Scroll locking that does not rubber-band.** `overflow: hidden` on `<body>` does
 *   nothing on iOS; the page still bounces and the sheet slides with it. `position: fixed`
 *   with the scroll offset preserved and restored is the only approach that holds, and it
 *   is ref-counted here so a confirm dialog opened from inside a sheet does not unlock the
 *   page when it closes.
 * - **VisualViewport awareness.** `interactive-widget=resizes-content` covers modern
 *   WebKit; everywhere else the keyboard slides over the layout without changing it, so the
 *   sheet measures the visual viewport itself and lifts its own content out of the way.
 * - **Velocity.** A sheet that only checks distance feels sticky. A flick that has barely
 *   travelled but is moving fast should dismiss, which is what the pointer-velocity test
 *   below does.
 */

import { clear, h, on, onCleanup } from '../../core/dom';
import { clamp } from '../../model/types';
import { bindReactive, capturePointer, icon, nextId, prefersReducedMotion } from './controls';
import type { Reactive } from './controls';

// MARK: - Body scroll locking

let scrollLockCount = 0;
let savedScrollY = 0;

function lockBodyScroll(): void {
  scrollLockCount += 1;
  if (scrollLockCount > 1) return;
  savedScrollY = window.scrollY;
  const style = document.body.style;
  style.setProperty('position', 'fixed');
  style.setProperty('top', `${-savedScrollY}px`);
  style.setProperty('left', '0');
  style.setProperty('right', '0');
  style.setProperty('width', '100%');
  document.body.classList.add('is-scroll-locked');
}

function unlockBodyScroll(): void {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount > 0) return;
  const style = document.body.style;
  for (const property of ['position', 'top', 'left', 'right', 'width']) {
    style.removeProperty(property);
  }
  document.body.classList.remove('is-scroll-locked');
  // Restoring the offset is the whole point: without it, dismissing a sheet drops the user
  // back at the top of a list they had scrolled halfway down.
  window.scrollTo(0, savedScrollY);
}

// MARK: - Background inerting

const SUPPORTS_INERT =
  typeof HTMLElement !== 'undefined' && 'inert' in HTMLElement.prototype;

interface InertRecord {
  readonly element: HTMLElement;
  readonly hadInert: boolean;
  readonly previousAriaHidden: string | null;
}

/**
 * Takes everything except `keep` out of the accessibility tree and out of tab order.
 *
 * `inert` is Safari 15.5+. On anything older the elements get `aria-hidden` instead, which
 * handles the screen-reader half; the focus trap below handles the keyboard half, so the
 * behaviour is complete either way.
 */
export function inertBackground(keep: HTMLElement): () => void {
  const records: InertRecord[] = [];
  const parent = keep.parentElement ?? document.body;
  for (const child of Array.from(parent.children)) {
    if (child === keep || !(child instanceof HTMLElement)) continue;
    const record: InertRecord = {
      element: child,
      hadInert: SUPPORTS_INERT ? (child as HTMLElement & { inert: boolean }).inert : false,
      previousAriaHidden: child.getAttribute('aria-hidden'),
    };
    records.push(record);
    if (SUPPORTS_INERT) (child as HTMLElement & { inert: boolean }).inert = true;
    else child.setAttribute('aria-hidden', 'true');
  }
  return () => {
    for (const record of records) {
      if (SUPPORTS_INERT) (record.element as HTMLElement & { inert: boolean }).inert = record.hadInert;
      if (record.previousAriaHidden === null) record.element.removeAttribute('aria-hidden');
      else record.element.setAttribute('aria-hidden', record.previousAriaHidden);
    }
  };
}

// MARK: - Focus trap

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

export function trapFocus(root: HTMLElement): () => void {
  const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') return;
    const focusable = focusableWithin(root);
    if (focusable.length === 0) {
      event.preventDefault();
      root.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === root)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Focus can also arrive from outside — a screen reader's rotor, or a stray programmatic
  // focus() — so the boundary is enforced on the way in as well as on Tab.
  const onFocusIn = (event: FocusEvent): void => {
    const target = event.target;
    if (!(target instanceof Node) || root.contains(target)) return;
    const focusable = focusableWithin(root);
    (focusable[0] ?? root).focus();
  };

  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('focusin', onFocusIn, true);

  const initial = focusableWithin(root)[0] ?? root;
  initial.focus({ preventScroll: true });

  return () => {
    document.removeEventListener('keydown', onKeyDown, true);
    document.removeEventListener('focusin', onFocusIn, true);
    previous?.focus({ preventScroll: true });
  };
}

// MARK: - Shared overlay plumbing

interface OverlayLayer {
  readonly root: HTMLElement;
  readonly surface: HTMLElement;
  open(): void;
  close(): void;
  readonly isOpen: boolean;
}

interface OverlayOptions {
  readonly rootClass: string;
  readonly surface: HTMLElement;
  readonly container?: HTMLElement;
  readonly dismissOnBackdrop: boolean;
  readonly dismissOnEscape: boolean;
  readonly onRequestClose: () => void;
  readonly onClosed?: () => void;
  /** Milliseconds to wait for the exit transition before removing the node regardless. */
  readonly exitTimeout?: number;
}

function createOverlay(options: OverlayOptions): OverlayLayer {
  const backdrop = h('div', { class: 'pl-overlay__backdrop', 'aria-hidden': 'true' });
  const root = h('div', { class: `pl-overlay ${options.rootClass}` }, backdrop, options.surface);

  let open = false;
  let releaseFocus: (() => void) | null = null;
  let releaseInert: (() => void) | null = null;
  let exitTimer = 0;

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !options.dismissOnEscape) return;
    event.preventDefault();
    options.onRequestClose();
  };

  backdrop.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    if (options.dismissOnBackdrop) options.onRequestClose();
  });

  const finishClose = (): void => {
    if (exitTimer !== 0) {
      clearTimeout(exitTimer);
      exitTimer = 0;
    }
    root.classList.remove('is-closing');
    root.remove();
    releaseInert?.();
    releaseInert = null;
    releaseFocus?.();
    releaseFocus = null;
    unlockBodyScroll();
    document.removeEventListener('keydown', onKeyDown);
    options.onClosed?.();
  };

  return {
    root,
    surface: options.surface,
    get isOpen() {
      return open;
    },
    open(): void {
      if (open) return;
      open = true;
      const container = options.container ?? document.body;
      container.appendChild(root);
      lockBodyScroll();
      releaseInert = inertBackground(root);
      document.addEventListener('keydown', onKeyDown);
      // Force a style resolution so the entry transition has a "from" state to run from;
      // without it the browser coalesces both classes and nothing animates.
      void root.getBoundingClientRect().height;
      root.classList.add('is-open');
      releaseFocus = trapFocus(options.surface);
    },
    close(): void {
      if (!open) return;
      open = false;
      root.classList.remove('is-open');
      root.classList.add('is-closing');
      const timeout = prefersReducedMotion() ? 0 : (options.exitTimeout ?? 320);
      if (timeout === 0) {
        finishClose();
        return;
      }
      // transitionend is the accurate signal; the timer is the guarantee that a sheet
      // whose transition was interrupted still leaves the DOM.
      const onEnd = (event: TransitionEvent): void => {
        if (event.target !== options.surface) return;
        options.surface.removeEventListener('transitionend', onEnd);
        finishClose();
      };
      options.surface.addEventListener('transitionend', onEnd);
      exitTimer = self.setTimeout(() => {
        options.surface.removeEventListener('transitionend', onEnd);
        finishClose();
      }, timeout + 60);
    },
  };
}

// MARK: - Sheet

export interface SheetOptions {
  readonly title: string;
  readonly kicker?: string;
  /**
   * Resting heights as a fraction of the available viewport, ascending. The sheet opens at
   * the last one and a downward drag steps through them before dismissing.
   */
  readonly detents?: readonly number[];
  readonly initialDetent?: number;
  readonly dismissible?: boolean;
  readonly container?: HTMLElement;
  readonly content?: Node;
  readonly onDismiss?: () => void;
  /** Fires whenever the sheet settles on a different detent. */
  readonly onDetentChange?: (index: number) => void;
}

export interface SheetHandle {
  readonly el: HTMLElement;
  /** Append your fields here, or pass `content`. */
  readonly body: HTMLElement;
  present(): void;
  dismiss(): void;
  setDetent(index: number): void;
  readonly isPresented: boolean;
  destroy(): void;
}

const DISMISS_VELOCITY = 0.55; // CSS px per millisecond
const DISMISS_FRACTION = 0.32;

export function sheet(options: SheetOptions): SheetHandle {
  const detents = (options.detents ?? [0.92]).map((value) => clamp(value, 0.2, 1)).sort((a, b) => a - b);
  const dismissible = options.dismissible !== false;
  const titleId = `pl-sheet-title-${nextId()}`;

  let detentIndex = clamp(options.initialDetent ?? detents.length - 1, 0, detents.length - 1);

  const grabber = h('div', { class: 'pl-sheet__grabber', 'aria-hidden': 'true' }, h('i'));
  const body = h('div', { class: 'pl-sheet__body scroll-y' });
  if (options.content !== undefined) body.appendChild(options.content);

  const closeButton = h(
    'button',
    {
      type: 'button',
      class: 'pl-sheet__close',
      'aria-label': 'Close',
      onClick: () => requestClose(),
    },
    icon('xmark', { size: 14 }),
  );

  const header = h(
    'header',
    { class: 'pl-sheet__header' },
    h(
      'div',
      { class: 'pl-sheet__titles' },
      options.kicker === undefined ? null : h('span', { class: 'kicker' }, options.kicker),
      h('h2', { class: 'pl-sheet__title', id: titleId }, options.title),
    ),
    dismissible ? closeButton : null,
  );

  const surface = h(
    'div',
    {
      class: 'pl-sheet',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': titleId,
      tabindex: '-1',
    },
    grabber,
    header,
    h('div', { class: 'pl-rule pl-rule--horizontal', role: 'presentation' }),
    body,
  );

  const overlay = createOverlay({
    rootClass: 'pl-overlay--sheet',
    surface,
    ...(options.container === undefined ? {} : { container: options.container }),
    dismissOnBackdrop: dismissible,
    dismissOnEscape: dismissible,
    onRequestClose: () => requestClose(),
    onClosed: () => {
      stopViewportTracking();
      options.onDismiss?.();
    },
  });

  function availableHeight(): number {
    return window.visualViewport?.height ?? window.innerHeight;
  }

  /** How far down the sheet sits, in CSS pixels, for a given detent. */
  function offsetFor(index: number): number {
    const fraction = detents[clamp(index, 0, detents.length - 1)] ?? 1;
    return Math.max(0, availableHeight() * (1 - fraction));
  }

  function applyOffset(offset: number, dragging: boolean): void {
    const height = Math.max(1, availableHeight());
    surface.style.setProperty('--sheet-y', `${offset}px`);
    // The backdrop tracks the sheet rather than the class, so a half-completed drag shows a
    // half-dimmed board instead of snapping between two opacities.
    overlay.root.style.setProperty(
      '--sheet-progress',
      String(clamp(1 - offset / height, 0, 1)),
    );
    surface.classList.toggle('is-dragging', dragging);
  }

  function setDetent(index: number): void {
    const next = clamp(index, 0, detents.length - 1);
    const changed = next !== detentIndex;
    detentIndex = next;
    applyOffset(offsetFor(next), false);
    if (changed) options.onDetentChange?.(next);
  }

  function requestClose(): void {
    if (!dismissible) return;
    overlay.close();
  }

  // --- Drag to dismiss --------------------------------------------------------------

  let dragPointer: number | null = null;
  let dragStartY = 0;
  let dragStartOffset = 0;
  let lastY = 0;
  let lastMoveAt = 0;
  let velocity = 0;
  /** A drag that started inside the scroller has to prove it is a drag before it takes over. */
  let dragFromBody = false;
  let dragCommitted = false;

  const INTERACTIVE = 'button, input, textarea, select, a[href], [role="slider"], [role="switch"], [role="spinbutton"]';

  const dragTarget = (target: EventTarget | null): 'chrome' | 'body' | null => {
    if (!(target instanceof Node)) return 'chrome';
    if (grabber.contains(target)) return 'chrome';
    // A control owns its own gesture; only the chrome around it drags the sheet.
    if (target instanceof Element && target.closest(INTERACTIVE) !== null) return null;
    if (body.contains(target)) return 'body';
    return 'chrome';
  };

  onCleanup(
    surface,
    on(surface, 'pointerdown', ((event: PointerEvent) => {
      if (!dismissible || dragPointer !== null) return;
      const origin = dragTarget(event.target);
      if (origin === null) return;
      dragPointer = event.pointerId;
      dragFromBody = origin === 'body';
      // Chrome drags own the pointer immediately. A drag that begins on the scroller does
      // not: capturing there would steal a flick the user meant as a scroll, so the pointer
      // is only claimed once the finger has travelled downward far enough to mean it.
      dragCommitted = !dragFromBody;
      dragStartY = event.clientY;
      lastY = event.clientY;
      lastMoveAt = event.timeStamp;
      velocity = 0;
      dragStartOffset = offsetFor(detentIndex);
      if (dragCommitted) capturePointer(surface, event.pointerId);
    }) as EventListener),
  );

  onCleanup(
    surface,
    on(surface, 'pointermove', ((event: PointerEvent) => {
      if (event.pointerId !== dragPointer) return;
      const delta = event.clientY - dragStartY;

      if (!dragCommitted) {
        if (body.scrollTop > 0 || delta < -2) {
          // The finger is scrolling the list, not moving the sheet. Let it go entirely.
          dragPointer = null;
          return;
        }
        if (delta < 6) return;
        dragCommitted = true;
        capturePointer(surface, event.pointerId);
      }

      if (delta < 0 && !dragFromBody) {
        // Upward past the tallest detent: resist rather than lift off the screen.
        applyOffset(Math.max(0, dragStartOffset + delta * 0.25), true);
      } else {
        applyOffset(Math.max(0, dragStartOffset + delta), true);
      }
      const elapsed = Math.max(1, event.timeStamp - lastMoveAt);
      velocity = (event.clientY - lastY) / elapsed;
      lastY = event.clientY;
      lastMoveAt = event.timeStamp;
      event.preventDefault();
    }) as EventListener, { passive: false }),
  );

  const endDrag = ((event: PointerEvent) => {
    if (event.pointerId !== dragPointer) return;
    dragPointer = null;
    surface.classList.remove('is-dragging');
    if (!dragCommitted) return;
    dragCommitted = false;

    const travelled = event.clientY - dragStartY;
    const height = Math.max(1, availableHeight());
    const flungDown = velocity > DISMISS_VELOCITY;
    const flungUp = velocity < -DISMISS_VELOCITY;

    if (flungUp && detentIndex < detents.length - 1) {
      setDetent(detentIndex + 1);
      return;
    }
    if (flungDown || travelled / height > DISMISS_FRACTION) {
      if (detentIndex > 0) setDetent(detentIndex - 1);
      else requestClose();
      return;
    }
    // Not enough to move: settle back onto whichever detent the sheet is nearest, so a
    // slow drag that crossed a boundary still lands where the finger left it.
    let nearest = detentIndex;
    let nearestDistance = Number.POSITIVE_INFINITY;
    const current = dragStartOffset + travelled;
    for (let index = 0; index < detents.length; index++) {
      const distance = Math.abs(offsetFor(index) - current);
      if (distance < nearestDistance) {
        nearest = index;
        nearestDistance = distance;
      }
    }
    setDetent(nearest);
  }) as EventListener;

  onCleanup(surface, on(surface, 'pointerup', endDrag));
  onCleanup(surface, on(surface, 'pointercancel', endDrag));

  // --- VisualViewport ---------------------------------------------------------------

  let viewportListenersAttached = false;

  const syncViewport = (): void => {
    const viewport = window.visualViewport;
    if (viewport !== undefined && viewport !== null) {
      // What the keyboard is covering: everything between the bottom of the visual viewport
      // and the bottom of the layout viewport.
      const covered = Math.max(0, window.innerHeight - (viewport.height + viewport.offsetTop));
      surface.style.setProperty('--sheet-keyboard-inset', `${Math.round(covered)}px`);
      surface.style.setProperty('--sheet-available', `${Math.round(viewport.height)}px`);
    } else {
      surface.style.setProperty('--sheet-keyboard-inset', '0px');
      surface.style.setProperty('--sheet-available', `${window.innerHeight}px`);
    }
    if (dragPointer === null) applyOffset(offsetFor(detentIndex), false);
  };

  const keepFieldVisible = (event: FocusEvent): void => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!body.contains(target)) return;
    // One frame after the keyboard animation starts, so the viewport has already shrunk.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    });
  };

  function startViewportTracking(): void {
    if (viewportListenersAttached) return;
    viewportListenersAttached = true;
    window.visualViewport?.addEventListener('resize', syncViewport);
    window.visualViewport?.addEventListener('scroll', syncViewport);
    window.addEventListener('orientationchange', syncViewport);
    surface.addEventListener('focusin', keepFieldVisible);
    syncViewport();
  }

  function stopViewportTracking(): void {
    if (!viewportListenersAttached) return;
    viewportListenersAttached = false;
    window.visualViewport?.removeEventListener('resize', syncViewport);
    window.visualViewport?.removeEventListener('scroll', syncViewport);
    window.removeEventListener('orientationchange', syncViewport);
    surface.removeEventListener('focusin', keepFieldVisible);
  }

  applyOffset(offsetFor(detentIndex), false);

  return {
    el: overlay.root,
    body,
    present(): void {
      overlay.open();
      startViewportTracking();
      setDetent(detentIndex);
    },
    dismiss(): void {
      overlay.close();
    },
    setDetent,
    get isPresented() {
      return overlay.isOpen;
    },
    destroy(): void {
      stopViewportTracking();
      overlay.close();
      clear(body);
    },
  };
}

// MARK: - Banner

export type BannerStyle = 'info' | 'success' | 'warning' | 'error';

export interface BannerOptions {
  readonly style: BannerStyle;
  readonly title: string;
  readonly message: string;
  readonly actionTitle?: string;
  readonly onAction?: () => void;
  readonly onDismiss: () => void;
}

export interface BannerHandle {
  readonly el: HTMLElement;
  destroy(): void;
}

const BANNER_SYMBOL: Readonly<Record<BannerStyle, string>> = {
  info: 'info.circle.fill',
  success: 'checkmark.circle.fill',
  warning: 'exclamationmark.triangle.fill',
  error: 'xmark.octagon.fill',
};

const BANNER_NAME: Readonly<Record<BannerStyle, string>> = {
  info: 'Information',
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
};

/** Success and info state a fact and get out of the way; warnings and errors stay. */
const BANNER_AUTO_DISMISS_MS = 4000;

/**
 * Transient message strip. Bordered and tinted, never bar-accented: a coloured rule down
 * the leading edge reads as a 2014 alert box and carries no meaning for anyone who cannot
 * see it. The glyph and the title do that work.
 */
export function banner(options: BannerOptions): BannerHandle {
  const autoDismisses = options.style === 'success' || options.style === 'info';

  const action =
    options.actionTitle === undefined || options.onAction === undefined
      ? null
      : h(
          'button',
          {
            type: 'button',
            class: 'pl-banner__action',
            onClick: () => {
              options.onAction?.();
              options.onDismiss();
            },
          },
          options.actionTitle,
        );

  const el = h(
    'div',
    {
      class: `pl-banner pl-banner--${options.style}`,
      role: options.style === 'warning' || options.style === 'error' ? 'alert' : 'status',
      'aria-live': options.style === 'warning' || options.style === 'error' ? 'assertive' : 'polite',
    },
    h('span', { class: 'pl-banner__glyph', 'aria-hidden': 'true' }, icon(BANNER_SYMBOL[options.style], { size: 18 })),
    h(
      'div',
      { class: 'pl-banner__text' },
      h('span', { class: 'kicker pl-banner__kind' }, BANNER_NAME[options.style]),
      h('span', { class: 'pl-banner__title' }, options.title),
      h('span', { class: 'pl-banner__message' }, options.message),
      action,
    ),
    h(
      'button',
      {
        type: 'button',
        class: 'pl-banner__close',
        'aria-label': 'Dismiss',
        onClick: () => options.onDismiss(),
      },
      icon('xmark', { size: 13 }),
    ),
  );

  // Swipe away: sideways or upward, the two directions that cannot be confused with
  // scrolling the screen underneath.
  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;

  onCleanup(
    el,
    on(el, 'pointerdown', ((event: PointerEvent) => {
      if (pointerId !== null) return;
      if (event.target instanceof HTMLElement && event.target.closest('button') !== null) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      capturePointer(el, event.pointerId);
      el.classList.add('is-dragging');
    }) as EventListener),
  );

  onCleanup(
    el,
    on(el, 'pointermove', ((event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = Math.min(0, event.clientY - startY);
      el.style.setProperty('transform', `translate3d(${dx}px, ${dy}px, 0)`);
      el.style.setProperty('opacity', String(clamp(1 - Math.abs(dx) / 220, 0.25, 1)));
    }) as EventListener),
  );

  const endSwipe = ((event: PointerEvent) => {
    if (event.pointerId !== pointerId) return;
    pointerId = null;
    el.classList.remove('is-dragging');
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    if (Math.abs(dx) > 70 || dy < -26) {
      options.onDismiss();
      return;
    }
    el.style.removeProperty('transform');
    el.style.removeProperty('opacity');
  }) as EventListener;

  onCleanup(el, on(el, 'pointerup', endSwipe));
  onCleanup(el, on(el, 'pointercancel', endSwipe));

  let timer = 0;
  if (autoDismisses) {
    const arm = (): void => {
      if (timer !== 0) clearTimeout(timer);
      timer = self.setTimeout(() => {
        timer = 0;
        options.onDismiss();
      }, BANNER_AUTO_DISMISS_MS);
    };
    arm();
    // A message someone is reading must not vanish mid-sentence — but it must still leave
    // once they look away, or a pinned banner sits over the board for the rest of the set.
    onCleanup(
      el,
      on(el, 'pointerenter', () => {
        if (timer !== 0) {
          clearTimeout(timer);
          timer = 0;
        }
      }),
    );
    onCleanup(el, on(el, 'pointerleave', arm));
  }
  onCleanup(el, () => {
    if (timer !== 0) clearTimeout(timer);
  });

  return {
    el,
    destroy(): void {
      if (timer !== 0) clearTimeout(timer);
      el.remove();
    },
  };
}

// MARK: - EmptyState

export interface EmptyStateOptions {
  readonly symbol: string;
  readonly title: string;
  readonly message: string;
  readonly actionTitle?: string;
  readonly onAction?: () => void;
}

export function emptyState(options: EmptyStateOptions): HTMLElement {
  return h(
    'div',
    { class: 'pl-state pl-state--empty' },
    h('span', { class: 'pl-state__glyph', 'aria-hidden': 'true' }, icon(options.symbol, { size: 34 })),
    h('h3', { class: 'pl-state__title' }, options.title),
    h('p', { class: 'pl-state__message' }, options.message),
    options.actionTitle === undefined || options.onAction === undefined
      ? null
      : h(
          'button',
          { type: 'button', class: 'pl-btn pl-btn--primary pl-state__action', onClick: () => options.onAction?.() },
          h('span', { class: 'pl-btn__label' }, options.actionTitle),
        ),
  );
}

// MARK: - ErrorState

export interface ErrorStateOptions {
  /** A thrown value. `message` and `recoverySuggestion` are read off it when present. */
  readonly error?: unknown;
  readonly title?: string;
  readonly message?: string;
  readonly recovery?: string;
  readonly retryTitle?: string;
  readonly onRetry?: () => void;
}

function describeError(error: unknown): { message: string; recovery: string | null } {
  if (error instanceof Error) {
    const suggestion = (error as Error & { recoverySuggestion?: unknown }).recoverySuggestion;
    return {
      message: error.message,
      recovery: typeof suggestion === 'string' && suggestion !== '' ? suggestion : null,
    };
  }
  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>;
    const message = typeof record['message'] === 'string' ? record['message'] : null;
    const recovery =
      typeof record['recoverySuggestion'] === 'string' ? record['recoverySuggestion'] : null;
    if (message !== null) return { message, recovery };
  }
  return { message: 'Something went wrong.', recovery: null };
}

export function errorState(options: ErrorStateOptions): HTMLElement {
  const described = options.error === undefined ? null : describeError(options.error);
  const message = options.message ?? described?.message ?? 'Something went wrong.';
  const recovery = options.recovery ?? described?.recovery ?? null;

  return h(
    'div',
    { class: 'pl-state pl-state--error callout callout--danger', role: 'group', 'aria-label': 'Error' },
    h('span', { class: 'pl-state__glyph', 'aria-hidden': 'true' }, icon('exclamationmark.triangle.fill', { size: 26 })),
    h('span', { class: 'kicker pl-state__kind' }, options.title ?? 'Something went wrong'),
    h('p', { class: 'pl-state__title' }, message),
    recovery === null ? null : h('p', { class: 'pl-state__message' }, recovery),
    options.onRetry === undefined
      ? null
      : h(
          'button',
          { type: 'button', class: 'pl-btn pl-btn--primary pl-state__action', onClick: () => options.onRetry?.() },
          h('span', { class: 'pl-btn__label' }, options.retryTitle ?? 'Try Again'),
        ),
  );
}

// MARK: - LoadingBar

export interface LoadingBarOptions {
  readonly label?: Reactive<string>;
  readonly tint?: string;
  /** 0…1 for a determinate bar; omit for the indeterminate sweep. */
  readonly progress?: Reactive<number>;
}

export interface LoadingBarHandle {
  readonly el: HTMLElement;
  setProgress(progress: number | null): void;
  destroy(): void;
}

/**
 * An indeterminate bar rather than a spinner: the system spinner is grey, sits at a fixed
 * size, and looks pasted onto a dark instrument surface. The sweep is a CSS transform
 * animation, so Reduce Motion replaces it with a breathing opacity through the stylesheet
 * rather than through a branch here.
 */
export function loadingBar(options: LoadingBarOptions = {}): LoadingBarHandle {
  const fill = h('i', { class: 'pl-loading__fill' });
  const track = h('div', { class: 'pl-loading__track', 'aria-hidden': 'true' }, fill);
  const label = options.label === undefined ? null : h('span', { class: 'pl-loading__label' });

  const el = h(
    'div',
    {
      class: 'pl-loading',
      role: 'progressbar',
      'aria-valuemin': '0',
      'aria-valuemax': '1',
      style: { '--loading-tint': options.tint ?? 'var(--accent-magenta)' },
    },
    track,
    label,
  );

  function setProgress(progress: number | null): void {
    if (progress === null || !Number.isFinite(progress)) {
      el.classList.add('is-indeterminate');
      el.removeAttribute('aria-valuenow');
      el.setAttribute('aria-valuetext', 'Working');
      fill.style.removeProperty('transform');
      return;
    }
    const value = clamp(progress, 0, 1);
    el.classList.remove('is-indeterminate');
    el.setAttribute('aria-valuenow', value.toFixed(3));
    el.setAttribute('aria-valuetext', `${Math.round(value * 100)} percent complete`);
    fill.style.setProperty('transform', `scaleX(${value})`);
  }

  if (label !== null && options.label !== undefined) {
    bindReactive(label, options.label, (value) => {
      label.textContent = value;
      el.setAttribute('aria-label', value);
    });
  } else {
    el.setAttribute('aria-label', 'Loading');
  }

  if (options.progress !== undefined) bindReactive(el, options.progress, setProgress);
  else setProgress(null);

  return {
    el,
    setProgress,
    destroy(): void {
      el.remove();
    },
  };
}

// MARK: - ProcessingOverlay

export interface ProcessingOverlayOptions {
  readonly title: string;
  readonly message?: string;
  readonly progress?: number | null;
  readonly cancelTitle?: string;
  readonly onCancel?: () => void;
  readonly container?: HTMLElement;
}

export interface ProcessingOverlayHandle {
  readonly el: HTMLElement;
  present(): void;
  dismiss(): void;
  setProgress(progress: number | null): void;
  setMessage(message: string): void;
  destroy(): void;
}

/**
 * Blocking progress for work the user must not interrupt by tapping through — rendering,
 * bouncing, exporting. Cancellable whenever the underlying job is; when it is not, there is
 * no cancel button rather than one that does nothing.
 */
export function processingOverlay(options: ProcessingOverlayOptions): ProcessingOverlayHandle {
  const titleId = `pl-processing-title-${nextId()}`;
  const bar = loadingBar({});
  const message = h('p', { class: 'pl-state__message' }, options.message ?? '');
  if (options.message === undefined) message.hidden = true;
  const percent = h('span', { class: 'pl-processing__percent numeric' });

  const card = h(
    'div',
    {
      class: 'pl-card pl-processing',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': titleId,
      tabindex: '-1',
    },
    h('span', { class: 'kicker' }, 'Working'),
    h('h3', { class: 'pl-state__title', id: titleId }, options.title),
    message,
    bar.el,
    percent,
    options.onCancel === undefined
      ? null
      : h(
          'button',
          {
            type: 'button',
            class: 'pl-btn pl-btn--secondary pl-processing__cancel',
            onClick: () => options.onCancel?.(),
          },
          h('span', { class: 'pl-btn__label' }, options.cancelTitle ?? 'Cancel'),
        ),
  );

  const overlay = createOverlay({
    rootClass: 'pl-overlay--processing',
    surface: card,
    ...(options.container === undefined ? {} : { container: options.container }),
    // A blocking job must not be dismissed by a stray tap on the scrim; cancelling is an
    // explicit act, and only when the job supports it.
    dismissOnBackdrop: false,
    dismissOnEscape: options.onCancel !== undefined,
    onRequestClose: () => options.onCancel?.(),
  });

  function setProgress(progress: number | null): void {
    bar.setProgress(progress);
    if (progress === null || !Number.isFinite(progress)) {
      percent.textContent = '';
      percent.hidden = true;
      return;
    }
    percent.hidden = false;
    percent.textContent = `${Math.round(clamp(progress, 0, 1) * 100)}%`;
  }

  setProgress(options.progress ?? null);

  return {
    el: overlay.root,
    present(): void {
      overlay.open();
    },
    dismiss(): void {
      overlay.close();
    },
    setProgress,
    setMessage(next: string): void {
      message.textContent = next;
      message.hidden = next === '';
    },
    destroy(): void {
      overlay.close();
      bar.destroy();
    },
  };
}

// MARK: - ConfirmDialog

export interface ConfirmDialogOptions {
  readonly title: string;
  readonly message: string;
  readonly confirmTitle: string;
  readonly cancelTitle?: string;
  readonly symbol?: string;
  readonly destructive?: boolean;
  readonly container?: HTMLElement;
  readonly onConfirm: () => void;
  readonly onCancel?: () => void;
}

export interface ConfirmDialogHandle {
  readonly el: HTMLElement;
  present(): void;
  dismiss(): void;
  destroy(): void;
}

/**
 * In-app destructive confirmation. A system `confirm()` would break out of the instrument's
 * surface at exactly the moment the user most needs to stay oriented — and on iOS it also
 * blocks the audio thread's main-thread work while it is up.
 */
export function confirmDialog(options: ConfirmDialogOptions): ConfirmDialogHandle {
  const destructive = options.destructive !== false;
  const titleId = `pl-confirm-title-${nextId()}`;
  const messageId = `pl-confirm-message-${nextId()}`;

  const card = h(
    'div',
    {
      class: ['pl-card', 'pl-confirm', destructive ? 'callout--danger' : 'callout--accent'],
      role: 'alertdialog',
      'aria-modal': 'true',
      'aria-labelledby': titleId,
      'aria-describedby': messageId,
      tabindex: '-1',
    },
    h(
      'span',
      { class: 'pl-state__glyph', 'aria-hidden': 'true' },
      icon(options.symbol ?? (destructive ? 'trash.fill' : 'checkmark.circle.fill'), { size: 24 }),
    ),
    h('h3', { class: 'pl-state__title', id: titleId }, options.title),
    h('p', { class: 'pl-state__message', id: messageId }, options.message),
    h(
      'div',
      { class: 'pl-confirm__actions' },
      h(
        'button',
        { type: 'button', class: 'pl-btn pl-btn--ghost', onClick: () => cancel() },
        h('span', { class: 'pl-btn__label' }, options.cancelTitle ?? 'Cancel'),
      ),
      h(
        'button',
        {
          type: 'button',
          class: destructive ? 'pl-btn pl-btn--destructive' : 'pl-btn pl-btn--primary',
          onClick: () => {
            overlay.close();
            options.onConfirm();
          },
        },
        h('span', { class: 'pl-btn__label' }, options.confirmTitle),
      ),
    ),
  );

  const overlay = createOverlay({
    rootClass: 'pl-overlay--confirm',
    surface: card,
    ...(options.container === undefined ? {} : { container: options.container }),
    dismissOnBackdrop: true,
    dismissOnEscape: true,
    onRequestClose: () => cancel(),
  });

  function cancel(): void {
    overlay.close();
    options.onCancel?.();
  }

  return {
    el: overlay.root,
    present(): void {
      overlay.open();
    },
    dismiss(): void {
      overlay.close();
    },
    destroy(): void {
      overlay.close();
    },
  };
}

// MARK: - Screen transitions

type ViewTransitionStarter = (callback: () => void | Promise<void>) => { finished: Promise<void> };

/**
 * TypeScript's DOM library declares `startViewTransition` as always present, which is the
 * one thing it is not — no shipping version of Safari has it. The property is read through
 * `unknown` so the feature check is a real runtime test rather than a type assertion the
 * compiler has already decided is unnecessary.
 */
function viewTransitionStarter(): ViewTransitionStarter | null {
  const candidate = (document as unknown as Record<string, unknown>)['startViewTransition'];
  return typeof candidate === 'function'
    ? ((candidate as ViewTransitionStarter).bind(document) as ViewTransitionStarter)
    : null;
}

export function supportsViewTransitions(): boolean {
  return viewTransitionStarter() !== null;
}

/**
 * Runs a DOM change inside a View Transition where the browser has one, and plainly where
 * it does not — which today includes every version of Safari that PadLab supports, so the
 * CSS fallback is the path most users take, not an afterthought.
 *
 * Reduce Motion skips the transition entirely: a cross-fade between two screens is exactly
 * the kind of movement the setting exists to remove.
 */
export function runViewTransition(update: () => void | Promise<void>): Promise<void> {
  const start = viewTransitionStarter();
  if (start === null || prefersReducedMotion()) {
    return Promise.resolve(update()).then(() => undefined);
  }
  return start(update).finished.catch(() => undefined);
}

/**
 * Replaces the contents of `container` with `next`, cross-fading through a View Transition
 * where available and through a one-shot CSS class everywhere else.
 */
export function swapScreen(container: HTMLElement, next: Node): Promise<void> {
  return runViewTransition(() => {
    clear(container);
    container.appendChild(next);
    if (supportsViewTransitions() || prefersReducedMotion()) return;
    // The fallback: a single enter animation, removed as soon as it finishes so a second
    // navigation re-triggers it.
    container.classList.remove('pl-screen-enter');
    void container.getBoundingClientRect().height;
    container.classList.add('pl-screen-enter');
    const done = (): void => {
      container.classList.remove('pl-screen-enter');
      container.removeEventListener('animationend', done);
    };
    container.addEventListener('animationend', done);
  });
}
